import notificationBannerService from '@/services/notificationBannerService';

const STORAGE_KEY = 'dismissedNotificationBanners';

const state = {
    currentBanner: null,
    dismissedBanners: [],
    loading: false,
    error: null,
    frontendBanners: []
};

const mutations = {
    SET_CURRENT_BANNER(state, banner) {
        state.currentBanner = banner;
    },

    SET_LOADING(state, loading) {
        state.loading = loading;
    },

    SET_ERROR(state, error) {
        state.error = error;
    },

    DISMISS_BANNER(state, bannerId) {
        if (!state.dismissedBanners.includes(bannerId)) {
            state.dismissedBanners.push(bannerId);
        }
        if (state.currentBanner && state.currentBanner.id === bannerId) {
            state.currentBanner = null;
        }
    },

    CLEAR_BANNER(state) {
        state.currentBanner = null;
    },

    SET_DISMISSED_BANNERS(state, banners) {
        state.dismissedBanners = banners || [];
    },

    ADD_FRONTEND_BANNER(state, banner) {
        const existingIndex = state.frontendBanners.findIndex((b) => b.id === banner.id);
        if (existingIndex >= 0) {
            state.frontendBanners[existingIndex] = banner;
        } else {
            state.frontendBanners.push(banner);
        }
    },

    REMOVE_FRONTEND_BANNER(state, bannerId) {
        state.frontendBanners = state.frontendBanners.filter((b) => b.id !== bannerId);
    },
};

const actions = {
    async fetchBanners({ commit, state, dispatch }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            await dispatch('loadDismissedBanners');

            // TODO: Uncomment when API endpoint is available
            // const response = await notificationBannerService.fetchBanners();
            // const banners = response.data || [];
            const banners = [];

            const allBanners = [...banners, ...state.frontendBanners];

            const activeBanners = allBanners
                .filter((banner) => !state.dismissedBanners.includes(banner.id))
                .sort((a, b) => (b.priority || 0) - (a.priority || 0));

            if (activeBanners.length > 0) {
                commit('SET_CURRENT_BANNER', activeBanners[0]);
            } else {
                commit('CLEAR_BANNER');
            }
        } catch (error) {
            commit('SET_ERROR', error);
            console.warn('Failed to fetch notification banners:', error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async dismissBanner({ commit, dispatch, state }, bannerId) {
        commit('DISMISS_BANNER', bannerId);
        await dispatch('saveDismissedBanners');

        await dispatch('refreshCurrentBanner');
    },

    loadDismissedBanners({ commit }) {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const dismissedBanners = JSON.parse(stored);
                commit('SET_DISMISSED_BANNERS', dismissedBanners);
            }
        } catch (error) {
            console.warn('Failed to load dismissed banners from localStorage:', error);
            commit('SET_DISMISSED_BANNERS', []);
        }
    },

    saveDismissedBanners({ state }) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state.dismissedBanners));
        } catch (error) {
            console.warn('Failed to save dismissed banners to localStorage:', error);
        }
    },

    addFrontendBanner({ commit, state, dispatch }, banner) {
        if (!banner.id) {
            console.error('Frontend banner must have an id');
            return;
        }
        if (!banner.message) {
            console.error('Frontend banner must have a message');
            return;
        }
        if (!banner.type) {
            banner.type = 'warning'
        }

        commit('ADD_FRONTEND_BANNER', banner);
        dispatch('refreshCurrentBanner');
    },

    removeFrontendBanner({ commit, state, dispatch }, bannerId) {
        commit('REMOVE_FRONTEND_BANNER', bannerId);
        dispatch('refreshCurrentBanner');
    },

    async refreshCurrentBanner({ commit, state, dispatch }) {
        await dispatch('loadDismissedBanners');

        // TODO: Uncomment when API endpoint is available
        // let apiBanners = [];
        // try {
        //     const response = await notificationBannerService.fetchBanners();
        //     apiBanners = response.data || [];
        // } catch (error) {
        //     console.warn('Failed to fetch API banners, using frontend banners only:', error);
        // }
        const apiBanners = [];

        const allBanners = [...apiBanners, ...state.frontendBanners];

        const activeBanners = allBanners
            .filter((banner) => !state.dismissedBanners.includes(banner.id))
            .sort((a, b) => (b.priority || 0) - (a.priority || 0));

        if (activeBanners.length > 0) {
            commit('SET_CURRENT_BANNER', activeBanners[0]);
        } else {
            commit('CLEAR_BANNER');
        }
    },
};

const getters = {
    activeBanner: (state) => {
        if (!state.currentBanner) {
            return null;
        }
        if (state.dismissedBanners.includes(state.currentBanner.id)) {
            return null;
        }
        return state.currentBanner;
    },

    hasActiveBanner: (state, getters) => {
        return getters.activeBanner !== null;
    },

    isLoading: (state) => state.loading,
    getError: (state) => state.error,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
